import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { User, Mail, Phone, Calendar, MapPin, Edit3, Save, X } from 'lucide-react';
import './ProfilePage.css';

function ProfilePage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    licenseNumber: '',
    vehicleNumber: '',
    city: '',
    registrationDate: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('http://localhost:3002/api/auth/profile', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfileData({
          firstName: data.user?.firstName || '',
          lastName: data.user?.lastName || '',
          email: data.user?.email || '',
          phone: data.user?.phone || '',
          licenseNumber: data.user?.licenseNumber || '',
          vehicleNumber: data.user?.vehicleNumber || '',
          city: data.user?.city || '',
          registrationDate: data.user?.createdAt ? new Date(data.user.createdAt).toLocaleDateString('de-DE') : ''
        });
      }
    } catch (error) {
      console.error('Profile fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('http://localhost:3002/api/auth/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileData)
      });

      if (response.ok) {
        setIsEditing(false);
        showSuccess('Profil erfolgreich aktualisiert!', {
          title: 'Gespeichert'
        });
      } else {
        const data = await response.json();
        showError(data.message || 'Profil konnte nicht aktualisiert werden', {
          title: 'Fehler beim Speichern'
        });
      }
    } catch (error) {
      console.error('Profile update error:', error);
      showError('Netzwerk-Fehler beim Speichern des Profils', {
        title: 'Verbindungsfehler'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    fetchProfile(); // Reset to original data
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Profil wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-header">
        <div className="profile-title">
          <User className="h-6 w-6 text-blue-600" />
          <h1>Mein Profil</h1>
        </div>
        
        <div className="profile-actions">
          {!isEditing ? (
            <button 
              onClick={() => setIsEditing(true)}
              className="btn-edit"
            >
              <Edit3 className="h-4 w-4" />
              Bearbeiten
            </button>
          ) : (
            <div className="edit-actions">
              <button 
                onClick={handleSave}
                disabled={saving}
                className="btn-save"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Speichern...' : 'Speichern'}
              </button>
              <button 
                onClick={handleCancel}
                className="btn-cancel"
              >
                <X className="h-4 w-4" />
                Abbrechen
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="profile-content">
        <div className="profile-grid">
          {/* Personal Information */}
          <div className="profile-section">
            <h2 className="section-title">
              <User className="h-5 w-5" />
              Persönliche Informationen
            </h2>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Vorname</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={profileData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="form-input"
                  />
                ) : (
                  <div className="form-value">{profileData.firstName || 'Nicht angegeben'}</div>
                )}
              </div>

              <div className="form-group">
                <label>Nachname</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={profileData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="form-input"
                  />
                ) : (
                  <div className="form-value">{profileData.lastName || 'Nicht angegeben'}</div>
                )}
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="profile-section">
            <h2 className="section-title">
              <Mail className="h-5 w-5" />
              Kontakt Informationen
            </h2>
            
            <div className="form-grid">
              <div className="form-group">
                <label>E-Mail</label>
                {isEditing ? (
                  <input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="form-input"
                  />
                ) : (
                  <div className="form-value">{profileData.email || 'Nicht angegeben'}</div>
                )}
              </div>

              <div className="form-group">
                <label>Telefon</label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="form-input"
                  />
                ) : (
                  <div className="form-value">{profileData.phone || 'Nicht angegeben'}</div>
                )}
              </div>
            </div>
          </div>

          {/* Professional Information */}
          <div className="profile-section">
            <h2 className="section-title">
              <Calendar className="h-5 w-5" />
              Berufliche Informationen
            </h2>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Führerschein Nummer</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={profileData.licenseNumber}
                    onChange={(e) => handleInputChange('licenseNumber', e.target.value)}
                    className="form-input"
                  />
                ) : (
                  <div className="form-value">{profileData.licenseNumber || 'Nicht angegeben'}</div>
                )}
              </div>

              <div className="form-group">
                <label>Fahrzeug Nummer</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={profileData.vehicleNumber}
                    onChange={(e) => handleInputChange('vehicleNumber', e.target.value)}
                    className="form-input"
                  />
                ) : (
                  <div className="form-value">{profileData.vehicleNumber || 'Nicht angegeben'}</div>
                )}
              </div>
            </div>
          </div>

          {/* Location & Registration */}
          <div className="profile-section">
            <h2 className="section-title">
              <MapPin className="h-5 w-5" />
              Standort & Registrierung
            </h2>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Stadt</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={profileData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className="form-input"
                  />
                ) : (
                  <div className="form-value">{profileData.city || 'Nicht angegeben'}</div>
                )}
              </div>

              <div className="form-group">
                <label>Registriert seit</label>
                <div className="form-value">{profileData.registrationDate || 'Unbekannt'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Stats */}
        <div className="profile-stats">
          <h2 className="section-title">
            📊 Profil Statistiken
          </h2>
          
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">0</div>
              <div className="stat-label">Fahrten insgesamt</div>
            </div>
            
            <div className="stat-card">
              <div className="stat-number">4.8⭐</div>
              <div className="stat-label">Durchschnittsbewertung</div>
            </div>
            
            <div className="stat-card">
              <div className="stat-number">0h</div>
              <div className="stat-label">Online Zeit (heute)</div>
            </div>
            
            <div className="stat-card">
              <div className="stat-number">₺0</div>
              <div className="stat-label">Verdienst (heute)</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
